'use client'

import Image from 'next/image'
import { SwiperSlide } from 'swiper/react'

import ZSwiper from '@/_components/universal/swiper/z-swiper'
import { IHighlight } from '@/app/[locale]/annual-programs/_constants'

/**
 * Individual highlight item component.
 * Displays a single highlight with optional title and image.
 *
 * @param highlight - Highlight data to display
 */
const AHighlightItem = ({ highlight }: { highlight: IHighlight }) => {
	return (
		<div
			className='medante-vital__highlights_content_item'
			key={`desktop-${highlight.description}`}
		>
			<div className='medante-vital__highlights_content_item_top'>
				{highlight.title && <p className='title'>{highlight.title}</p>}
				{highlight.image && (
					<Image
						unoptimized={true}
						src={highlight.image.src}
						alt={highlight.image.alt}
						height={highlight.image.height}
						width={highlight.image.width}
					/>
				)}
			</div>

			<p>{highlight.description}</p>
		</div>
	)
}

/**
 * Props for the ZAPDetailHighlights component.
 */
interface ZAPDetailHighlightsProps {
	/** Array of highlights to display */
	highlights: IHighlight[]
}

/**
 * Universal highlights section for annual program details.
 * Displays key program features in a grid on desktop and carousel on mobile.
 * Shared across all annual program detail pages (Balance, Mini, Vital).
 *
 * Features displayed:
 * - 35 ambulances
 * - Holistic approach
 * - Available appointments
 * - Free parking
 * - Year-round care
 *
 * @param highlights - Array of highlight items to display
 *
 * @example
 * ```typescript
 * <ZAPDetailHighlights highlights={MEDANTE_MINI_HIGHLIGHTS} />
 * ```
 */
export default function ZAPDetailHighlights({ highlights }: ZAPDetailHighlightsProps) {
	return (
		<section className='medante-vital__highlights wrapper2 mgt-10lg mgt-50 mgb-50 mgb-30lg'>
			{/* Desktop Grid View */}
			<div className='medante-vital__highlights_content hide_lgm'>
				{highlights.map(highlight => (
					<AHighlightItem
						highlight={highlight}
						key={`desktop-${highlight.description}`}
					/>
				))}
			</div>

			{/* Mobile Carousel View */}
			<div className='medante-vital__highlights_content show_lgm'>
				<ZSwiper
					spaceBetween={16}
					initialSlidesPerView={1}
					autoplay={true}
					autoplayDelay={2500}
					loop={true}
					showArrows={false}
					showProgressbar={false}
					showCounter={false}
				>
					{highlights.map((highlight, index) => (
						<SwiperSlide key={index}>
							<AHighlightItem
								highlight={highlight}
								key={`mobile-${highlight.description}`}
							/>
						</SwiperSlide>
					))}
				</ZSwiper>
			</div>
		</section>
	)
}
